import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { showSuccess, showValidationError, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { payrollPaymentService, toPayrollPaymentMonthYear } from '@/services/payrollPaymentService'
import { employeesService } from '@/services/employeesService'
import type {
  PayrollPaymentVm,
  GetPaymentAmountEmployeeItem,
  PayrollPaymentDetailItem,
  PayrollPaymentLineItem,
} from '@/api/types/payroll.types'
import type { DepartmentLookup } from '@/api/types'

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export const PayrollPaymentDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const isNew = id === 'new' || !id
  const editId = !isNew && id ? parseInt(id, 10) : 0

  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [payrollMonthYear, setPayrollMonthYear] = useState(getDefaultMonthYear())
  const [departmentId, setDepartmentId] = useState(0)
  const [paymentDate, setPaymentDate] = useState(() => {
    const d = new Date()
    return d.toISOString().slice(0, 10)
  })
  const [employeeList, setEmployeeList] = useState<GetPaymentAmountEmployeeItem[]>([])
  const [paymentAmounts, setPaymentAmounts] = useState<Record<number, string>>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveDisabled, setSaveDisabled] = useState(false)
  const [, _setLoadedDetailsForEdit] = useState<PayrollPaymentDetailItem[]>([])
  const loadedDetailsRef = useRef<PayrollPaymentDetailItem[]>([])
  const [editLines, setEditLines] = useState<PayrollPaymentLineItem[]>([])
  const [empIdToName, setEmpIdToName] = useState<Record<number, string>>({})
  const [masterAmount, setMasterAmount] = useState<number>(0)

  // Load departments
  useEffect(() => {
    let cancelled = false
    employeesService.getDepartmentLookups().then((list) => {
      if (!cancelled && list) setDepartments(list)
    }).catch(() => { if (!cancelled) setDepartments([]) })
    return () => { cancelled = true }
  }, [])

  // When month/year and department change, call GetPaymentAmount (create mode only)
  useEffect(() => {
    if (isNew && !branchId) return
    if (!branchId || !payrollMonthYear || payrollMonthYear.length < 7) {
      setEmployeeList([])
      setPaymentAmounts({})
      setSaveDisabled(false)
      return
    }
    if (!isNew) return
    let cancelled = false
    setEmployeeList([])
    setPaymentAmounts({})
    payrollPaymentService
      .getPaymentAmount(payrollMonthYear, departmentId, branchId)
      .then((list) => {
        if (cancelled) return
        setEmployeeList(list)
        if (list.length === 0) {
          showError(t('payrollPaymentPage.errorPayrollNotGeneratedTitle'), t('payrollPaymentPage.errorPayrollNotGeneratedMessage'))
          setSaveDisabled(true)
        } else {
          setSaveDisabled(false)
          const fromEdit = loadedDetailsRef.current
          const initial: Record<number, string> = {}
          list.forEach((emp) => {
            const detail = fromEdit.find((d) => d.employeeId === emp.employeeId)
            initial[emp.employeeId] = detail != null ? String(detail.amount) : String(emp.remainingAmount)
          })
          setPaymentAmounts(initial)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEmployeeList([])
          setSaveDisabled(false)
        }
      })
    return () => { cancelled = true }
  }, [isNew, branchId, payrollMonthYear, departmentId])


  // Load existing record in edit mode: getById (master) -> getLinesByPaymentId (lines) -> getPaymentAmount (names)
  useEffect(() => {
    if (isNew || !editId || !branchId) return
    let cancelled = false
    setLoading(true)
    setEditLines([])
    setEmpIdToName({})
    payrollPaymentService
      .getById(editId)
      .then((data) => {
        if (cancelled) return
        const my = data.payrollMonthYear ?? ''
        let formMonthYear = getDefaultMonthYear()
        if (my.length >= 5) {
          if (my.includes('-')) {
            const [a, b] = my.split('-')
            formMonthYear = a.length === 4 ? `${a}-${b.padStart(2, '0')}` : `${b}-${a.padStart(2, '0')}`
          } else {
            formMonthYear = my
          }
        }
        setPayrollMonthYear(formMonthYear)
        const dateVal = data.date ?? (data as any).paymentDate ?? ''
        setPaymentDate(dateVal ? dateVal.slice(0, 10) : new Date().toISOString().slice(0, 10))
        setMasterAmount(data.amount ?? 0)
        return Promise.all([
          payrollPaymentService.getLinesByPaymentId(editId),
          payrollPaymentService.getPaymentAmount(formMonthYear, 0, branchId),
        ])
      })
      .then((result: [PayrollPaymentLineItem[], GetPaymentAmountEmployeeItem[]] | undefined) => {
        if (cancelled || !result) return
        const [lines, empList] = result
        setEditLines(lines ?? [])
        const nameMap: Record<number, string> = {}
        ;(empList ?? []).forEach((e: GetPaymentAmountEmployeeItem) => {
          nameMap[e.employeeId] = e.empName ?? ''
        })
        setEmpIdToName(nameMap)
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err.response?.data?.message || t('payrollPaymentPage.loadFailed'))
          navigate('/payroll/payroll-payment')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [isNew, editId, branchId, navigate])

  const departmentOptions = useMemo(
    () => [
      { value: '0', label: t('payrollPaymentPage.placeholderAll') },
      ...departments.filter((d) => d.id).map((d) => ({
        value: String(d.id),
        label: d.lookupNameEn || d.lookupNameAr || String(d.id),
      })),
    ],
    [departments, t]
  )

  const setPaymentAmountForEmployee = (employeeId: number, value: string) => {
    setPaymentAmounts((prev) => ({ ...prev, [employeeId]: value }))
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('payrollPaymentPage.validationBranch'))
      return
    }
    if (!payrollMonthYear || payrollMonthYear.length < 7) {
      await showValidationError(t('payrollPaymentPage.validationMonthYear'))
      return
    }
    if (saveDisabled || employeeList.length === 0) {
      await showValidationError(t('payrollPaymentPage.validationNoData'))
      return
    }
    if (!paymentDate || paymentDate.length < 10) {
      await showValidationError(t('payrollPaymentPage.validationPaymentDate'))
      return
    }

    const details: PayrollPaymentDetailItem[] = []
    let totalAmount = 0
    for (const emp of employeeList) {
      const raw = paymentAmounts[emp.employeeId] ?? ''
      if (!raw.trim()) continue
      const amt = parseFloat(raw)
      if (Number.isNaN(amt) || amt < 0) {
        await showValidationError(t('payrollPaymentPage.validationAmountFor', { name: emp.empName ?? '' }))
        return
      }
      if (amt > emp.remainingAmount) {
        await showValidationError(
          t('payrollPaymentPage.validationExceed', { name: emp.empName ?? '', max: emp.remainingAmount })
        )
        return
      }
      details.push({ employeeId: emp.employeeId, amount: amt })
      totalAmount += amt
    }

    if (details.length === 0) {
      await showValidationError(t('payrollPaymentPage.validationAtLeastOne'))
      return
    }

    setSaving(true)
    try {
      const apiMonthYear = toPayrollPaymentMonthYear(payrollMonthYear)
      const payload: PayrollPaymentVm = {
        id: isNew ? 0 : editId,
        payrollMonthYear: apiMonthYear,
        paymentDate: new Date(paymentDate + 'T00:00:00').toISOString(),
        amount: totalAmount,
        branchId,
        payrollPaymentDetails: details,
      }
      console.log('Payroll Payment (Save/Update) Payload:', JSON.stringify(payload, null, 2))
      if (isNew) {
        await payrollPaymentService.create(payload)
        await showSuccess(t('payrollPaymentPage.savedTitle'), t('payrollPaymentPage.savedMessage'))
      } else {
        await payrollPaymentService.update(payload)
        await showSuccess(t('payrollPaymentPage.updatedTitle'), t('payrollPaymentPage.updatedMessage'))
      }
      navigate('/payroll/payroll-payment')
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('payrollPaymentPage.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const totalPaymentAmount = useMemo(() => {
    let sum = 0
    employeeList.forEach((emp) => {
      const raw = paymentAmounts[emp.employeeId] ?? ''
      const amt = parseFloat(raw)
      if (!Number.isNaN(amt) && amt > 0) sum += amt
    })
    return sum
  }, [employeeList, paymentAmounts])

  const getAmountError = (emp: GetPaymentAmountEmployeeItem): string | null => {
    const raw = paymentAmounts[emp.employeeId] ?? ''
    if (!raw.trim()) return null
    const amt = parseFloat(raw)
    if (Number.isNaN(amt) || amt < 0) return t('payrollPaymentPage.invalidAmount')
    if (amt > emp.remainingAmount) return t('payrollPaymentPage.maxRemaining', { amount: emp.remainingAmount })
    return null
  }

  const req = t('payrollPaymentPage.requiredMark')

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('payrollPaymentPage.pageTitle')} description={t('payrollPaymentPage.formDescription')} />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('payrollPaymentPage.selectBranchForm')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t('payrollPaymentPage.pageTitle')} description={t('payrollPaymentPage.formDescription')} />

      <Card>
        <CardHeader>
          <CardTitle>{isNew ? t('payrollPaymentPage.addTitle') : t('payrollPaymentPage.viewTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">{t('payrollPaymentPage.loading')}</div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>{req} {t('payrollPaymentPage.labelMonthYear')}</Label>
                  <Input
                    type="month"
                    value={payrollMonthYear}
                    onChange={(e) => !isNew ? undefined : setPayrollMonthYear(e.target.value)}
                    readOnly={!isNew}
                    className={!isNew ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('payrollPaymentPage.labelDepartment')}</Label>
                  <SearchableSelect
                    options={departmentOptions}
                    value={departmentId ? String(departmentId) : '0'}
                    onValueChange={(v) => !isNew ? undefined : setDepartmentId(v ? Number(v) : 0)}
                    placeholder={t('payrollPaymentPage.placeholderAll')}
                    searchPlaceholder={t('payrollPaymentPage.searchShortPlaceholder')}
                    emptyMessage={t('payrollPaymentPage.emptyDepartment')}
                    disabled={!isNew}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{req} {t('payrollPaymentPage.labelPaymentDate')}</Label>
                  <Input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => !isNew ? undefined : setPaymentDate(e.target.value)}
                    readOnly={!isNew}
                    className={!isNew ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('payrollPaymentPage.labelTotalPayrollAmount')}</Label>
                  <Input
                    type="text"
                    readOnly
                    value={String(isNew ? totalPaymentAmount : masterAmount)}
                    className="bg-muted"
                  />
                </div>
              </div>

              {isNew && employeeList.length > 0 && (
                <>
                  <Label>{t('payrollPaymentPage.hintEmployeeAmounts')}</Label>
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            {t('payrollPaymentPage.colEmployeeName')}
                          </TableHead>
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            {t('payrollPaymentPage.colTotalAmount')}
                          </TableHead>
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            {t('payrollPaymentPage.colRemainingAmount')}
                          </TableHead>
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            {t('payrollPaymentPage.colPaymentAmount')} {req}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {employeeList.map((emp) => (
                          <TableRow
                            key={emp.employeeId}
                            className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                          >
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {emp.empName}
                            </TableCell>
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {emp.totalAmount}
                            </TableCell>
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {emp.remainingAmount}
                            </TableCell>
                            <TableCell className="px-4 py-2 text-center align-middle">
                              <div className="flex justify-center">
                                <Input
                                  type="number"
                                  min={0}
                                  max={emp.remainingAmount}
                                  step="any"
                                  placeholder="0"
                                  value={paymentAmounts[emp.employeeId] ?? ''}
                                  onChange={(e) => setPaymentAmountForEmployee(emp.employeeId, e.target.value)}
                                  className="w-28 text-center"
                                />
                              </div>
                              {getAmountError(emp) && (
                                <p className="text-xs text-destructive mt-1 text-center">{getAmountError(emp)}</p>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}

              {!isNew && editLines.length > 0 && (
                <>
                  <Label>{t('payrollPaymentPage.paymentLinesLabel')}</Label>
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            {t('payrollPaymentPage.colEmployeeName')}
                          </TableHead>
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            {t('payrollPaymentPage.colRemainingAmount')}
                          </TableHead>
                          <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                            {t('payrollPaymentPage.colPaymentAmount')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {editLines.map((line) => (
                          <TableRow
                            key={line.id ?? line.empId}
                            className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                          >
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {empIdToName[line.empId] ?? t('payrollPaymentPage.employeeFallback', { id: line.empId })}
                            </TableCell>
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {line.remainingAmount ?? '—'}
                            </TableCell>
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {line.amount}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => navigate('/payroll/payroll-payment')}>
                  {isNew ? t('payrollPaymentPage.cancel') : t('payrollPaymentPage.back')}
                </Button>
                {isNew && (
                  <Button onClick={handleSave} disabled={saving || saveDisabled}>
                    {saving ? t('payrollPaymentPage.saving') : t('payrollPaymentPage.save')}
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
